import { useMutation } from "@apollo/client";
import { DownloadBox } from "atoms";
import {
    constantsData,
    identifiersData,
    namesData,
    statusTypesData,
} from "data";
import { exportRevenueReportsMutation } from "graph";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { openAlert } from "redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "utils";

const useExportRevenueReports = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportRevenueReportsAction, { loading }] = useMutation(exportRevenueReportsMutation);

    const {
        exportRevenueReportsPending: exportRevenueReportsPendingToastName,
        exportRevenueReportsSuccess: exportRevenueReportsSuccessToastName,
    } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportRevenueReports = async (ids) => {
        setExporting(true);

        try {
            await exportRevenueReportsAction({
                onCompleted: ({ exportRevenueReport }) => {
                    setExporting(false);

                    const randomId = getUniqueRandomId();

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportRevenueReportsSuccessToastName}_${toastId}_${randomId}`}
                                link={exportRevenueReport}
                                message={t(
                                    "messages.downloadMsgs.download",
                                    { entity: t("revenueReports.name") },
                                )}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportRevenueReportsSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(openAlert(
                        t(
                            "messages.exportMsgs.success",
                            { entity: t("revenueReports.name") },
                        ),
                        statusTypesData.success,
                    ));
                },
                variables: { reportsIds: ids || [] },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportRevenueReportsPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(
                    () => t(
                        "messages.exportMsgs.pending",
                        { entity: t("revenueReports.name") },
                    ),
                    { toastId: `${exportRevenueReportsPendingToastName}_${toastId}` },
                );
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportRevenueReports };
};

export default useExportRevenueReports;
